import { Link } from "react-router-dom";
import { X, ArrowRight, Trash2, Minus, Plus } from "lucide-react";
import { useCart, formatINR } from "../context/CartContext";
import SafeImg from "./SafeImg";

export default function CartDrawer() {
  const { items, updateQuantity, removeItem, totals, drawerOpen, closeDrawer } = useCart();

  return (
    <>
      <div
        className={`fixed inset-0 z-[70] bg-ink-900/45 backdrop-blur-sm transition-opacity duration-300 ${
          drawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeDrawer}
        data-testid="cart-drawer-overlay"
      />
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 bottom-0 z-[71] w-full sm:max-w-md bg-cream-50 shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-blush-100 bg-cream-50/95 backdrop-blur">
          <div>
            <span className="font-script text-2xl text-blush-400 leading-none">your edit</span>
            <h3 className="font-heading text-2xl text-ink-900 leading-tight">
              Cart {totals.count > 0 && <span className="text-base text-ink-500 font-body">({totals.count})</span>}
            </h3>
          </div>
          <button
            onClick={closeDrawer}
            data-testid="cart-drawer-close"
            aria-label="Close cart"
            className="h-10 w-10 grid place-items-center rounded-full hover:bg-blush-50 text-ink-700"
          >
            <X size={18} strokeWidth={1.6} />
          </button>
        </header>

        {items.length === 0 ? (
          <div className="flex-1 grid place-items-center px-6 text-center">
            <div>
              <p className="font-heading text-2xl text-ink-900">Your cart is empty</p>
              <p className="mt-2 font-body text-sm text-ink-500">Tap a piece you love and it'll wait here softly.</p>
              <Link
                to="/shop"
                onClick={closeDrawer}
                className="btn-maroon mt-6 inline-flex items-center gap-2"
                data-testid="cart-drawer-shop-btn"
              >
                Browse the collection <ArrowRight size={14} strokeWidth={1.6} />
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
              {items.map((it) => (
                <article
                  key={it.cartKey || `${it.id}-${it.size}`}
                  data-testid={`cart-drawer-item-${it.id}`}
                  className="flex items-stretch gap-3"
                >
                  <Link to={`/product/${it.id}`} onClick={closeDrawer} className="block w-20 shrink-0">
                    <SafeImg src={it.image} alt={it.name} className="aspect-[4/5] w-full object-cover rounded-xl" />
                  </Link>
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      <Link to={`/product/${it.id}`} onClick={closeDrawer}>
                        <h4 className="font-heading text-base text-ink-900 leading-tight line-clamp-2">{it.name}</h4>
                      </Link>
                      <p className="text-[11px] font-body text-ink-500 mt-0.5">Size {it.size}</p>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1 px-1.5 py-1 rounded-full border border-blush-100 bg-white">
                        <button onClick={() => updateQuantity(it.cartKey || it.id, Math.max(1, it.quantity - 1))} className="h-6 w-6 grid place-items-center rounded-full hover:bg-blush-50">
                          <Minus size={11} strokeWidth={1.7} />
                        </button>
                        <span className="w-5 text-center font-body text-xs">{it.quantity}</span>
                        <button onClick={() => updateQuantity(it.cartKey || it.id, it.quantity + 1)} className="h-6 w-6 grid place-items-center rounded-full hover:bg-blush-50">
                          <Plus size={11} strokeWidth={1.7} />
                        </button>
                      </div>
                      <p className="font-body text-sm text-ink-900">{formatINR(it.price * it.quantity)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeItem(it.cartKey || it.id)}
                    aria-label="Remove"
                    className="h-8 w-8 self-start grid place-items-center rounded-full text-blush-600 hover:bg-blush-50 shrink-0"
                  >
                    <Trash2 size={13} strokeWidth={1.6} />
                  </button>
                </article>
              ))}
            </div>

            <footer className="border-t border-blush-100 px-5 py-4 bg-cream-50/95 backdrop-blur space-y-3">
              <dl className="text-sm font-body text-ink-700 space-y-1">
                <div className="flex justify-between"><dt>Subtotal</dt><dd>{formatINR(totals.subtotal)}</dd></div>
                {totals.discount > 0 && (
                  <div className="flex justify-between text-blush-600"><dt>Discount</dt><dd>- {formatINR(totals.discount)}</dd></div>
                )}
                <div className="flex justify-between font-heading text-lg text-ink-900 pt-2 border-t border-blush-100">
                  <dt>Total</dt>
                  <dd>{formatINR(totals.total)}</dd>
                </div>
              </dl>
              <div className="flex items-center gap-2">
                <Link
                  to="/cart"
                  onClick={closeDrawer}
                  data-testid="cart-drawer-view-btn"
                  className="btn-ghost-rose text-xs flex-1 text-center"
                >
                  View cart
                </Link>
                <Link
                  to="/checkout"
                  onClick={closeDrawer}
                  data-testid="cart-drawer-checkout-btn"
                  className="btn-maroon text-xs flex-1 inline-flex items-center justify-center gap-1.5"
                >
                  Checkout <ArrowRight size={12} strokeWidth={1.7} />
                </Link>
              </div>
            </footer>
          </>
        )}
      </aside>
    </>
  );
}
